/**
 * Confirmed delivery of an Atlas handoff's context to Crisp (AS-11647).
 *
 * Crisp disconnects a page that has had no click, key or mouse move for
 * 10 minutes ("You were inactive"), and after an hour reconnects only on a
 * click inside the chatbox. Typing to Atlas does not count: Atlas is an
 * iframe, and Crisp listens on warbler's own window. While disconnected Crisp
 * silently DROPS `set` and `message:send`, so the summary was lost while the
 * merchant was told the team had it. A `session:data` write only reads back
 * (`get`) once Crisp's server accepted it: that read-back is the signal.
 *
 * Mirrors atlas frontend/src/config/crispDelivery.ts; kept free of Vue so
 * mocha can load it.
 */
export interface CrispSdkLike {
  push: (command: unknown[]) => void;
  get?: (...args: unknown[]) => unknown;
}

/** `handoff_at` in session data: the stamp read back to confirm a write landed. */
export const HANDOFF_STAMP_KEY = 'handoff_at';
const POLL_MS = 250;
const RESEND_MS = 2000;
const GIVE_UP_MS = 10 * 60 * 1000;

let delivery: { promise: Promise<boolean>; cancel: () => void } | null = null;
// Bumped by every cancel: a handoff still waiting for Crisp to boot checks it
// before it writes anything (MR !2880 review).
let cancellations = 0;

/** Read before an await; a different value after it means a logout happened in between. */
export function handoffCancellations(): number {
  return cancellations;
}

/**
 * Write the handoff's session data, then send `messages`, only once Crisp
 * really has them: `nudge` Crisp's activity listener (which reconnects an
 * idle socket), re-send the data until its fresh stamp reads back — including
 * after the merchant clicks Crisp's "Reconnect" — and only then send the
 * messages, exactly once. A newer handoff replaces this one.
 *
 * Without `get` (a queue l.js has not swapped yet) nothing can be confirmed:
 * the pushes go straight in, as before.
 */
export function deliverHandoffContext(
  crisp: CrispSdkLike,
  entries: unknown[][],
  messages: string[],
  nudge: () => void,
): void {
  if (delivery) delivery.cancel();
  const sendMessages = () => {
    // After session:data, so support sees the context next to the text.
    messages.forEach((text) => crisp.push(['do', 'message:send', ['text', text]]));
  };
  if (typeof crisp.get !== 'function') {
    crisp.push(['set', 'session:data', [entries]]);
    sendMessages();
    delivery = null;
    return;
  }
  const read = crisp.get.bind(crisp);
  const stamp = `${new Date().toISOString()}#${Math.random().toString(36).slice(2, 8)}`;
  const write = () => {
    nudge();
    crisp.push(['set', 'session:data', [[...entries, [HANDOFF_STAMP_KEY, stamp]]]]);
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  let settle: (delivered: boolean) => void = () => undefined;
  const promise = new Promise<boolean>((resolve) => {
    settle = (delivered) => {
      if (timer !== undefined) clearTimeout(timer);
      resolve(delivered);
    };
  });
  const started = Date.now();
  let lastWrite = started;
  const poll = () => {
    if (read('session:data', HANDOFF_STAMP_KEY) === stamp) {
      sendMessages();
      settle(true);
      return;
    }
    const now = Date.now();
    if (now - started >= GIVE_UP_MS) {
      settle(false);
      return;
    }
    if (now - lastWrite >= RESEND_MS) {
      write();
      lastWrite = now;
    }
    timer = setTimeout(poll, POLL_MS);
  };
  write();
  timer = setTimeout(poll, POLL_MS);
  delivery = { promise, cancel: () => settle(false) };
}

/**
 * Did the last handoff's context reach Crisp within `withinMs`? Delivery
 * carries on after this answers false; the caller only decides what to tell
 * the merchant now.
 */
export async function handoffContextDelivered(withinMs: number): Promise<boolean> {
  if (!delivery) return true;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<boolean>((resolve) => {
    timeout = setTimeout(() => resolve(false), withinMs);
  });
  try {
    return await Promise.race([delivery.promise, late]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

/**
 * Stop any delivery in flight. Logout calls it: a poll left running would
 * write this merchant's shop and summary into the next person's Crisp session
 * once it reconnects.
 */
export function cancelHandoffDelivery(): void {
  cancellations += 1;
  if (delivery) delivery.cancel();
  delivery = null;
}
